import React from 'react';
import {
    INSTITUTION_KEYS,
    institutionLabel,
} from '@/shared/constants/institutions';
import DataTable from './DataTable';
import { whole } from './format';

const RECEIVING = [...INSTITUTION_KEYS, 'unknown'];
const receivingLabel = (key) =>
    key === 'unknown' ? 'Unknown' : institutionLabel(key);

// Six shade steps, 0 for an empty route. A route with any transfers is at
// least step 1, so a quiet route is never drawn like an empty one.
const STEPS = 5;
const heatLevel = (count, max) =>
    count === 0 ? 0 : Math.max(1, Math.round((count / max) * STEPS));

/**
 * Transfers from each giving institution (rows) to each receiving
 * institution (columns).
 *
 * The count is printed in every cell; the shading only helps the eye find the
 * busy routes, so nothing depends on seeing it. The strongest step is a tint
 * light enough for navy text to keep 4.5:1.
 */
function FlowMatrix({ flow }) {
    const max = Math.max(
        1,
        ...INSTITUTION_KEYS.flatMap((from) =>
            RECEIVING.map((to) => flow[from]?.[to] || 0)
        )
    );
    const rows = INSTITUTION_KEYS.map((from) => {
        const counts = RECEIVING.map((to) => flow[from]?.[to] || 0);
        return {
            key: from,
            label: institutionLabel(from),
            counts,
            values: [...counts, counts.reduce((sum, n) => sum + n, 0)],
        };
    });

    return (
        <section aria-labelledby="report-flow" className="mb-4">
            <h3 id="report-flow" className="h6">
                Transfers between institutions
            </h3>
            <p className="text-muted small">
                Each row is the institution that gave, each column the one that
                received. Unknown is a transfer completed with no accepted
                request. Darker cells are busier routes; every cell shows its
                count.
            </p>
            <DataTable
                caption="Transfers from each giving institution to each receiving institution"
                corner="Giving institution"
                columns={[...RECEIVING.map(receivingLabel), 'All receiving']}
                rows={rows}
                renderCell={(value, row, index, columns) => {
                    const isTotal = index === RECEIVING.length;
                    return (
                        <td
                            key={columns[index]}
                            className={
                                isTotal
                                    ? 'fw-bold'
                                    : `afe-heat-${heatLevel(row.counts[index], max)}`
                            }
                        >
                            {whole(value)}
                        </td>
                    );
                }}
            />
        </section>
    );
}

export default FlowMatrix;
